import React, { useState, useEffect } from 'react';
import { 
  BoltIcon, 
  SunIcon,
  ChartBarIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import PowerIrradianceChart from '../components/charts/PowerIrradianceChart';
import GenerationTrendChart from '../components/charts/GenerationTrendChart';
import { technicalApi, PowerIrradianceResponse, DailyExportResponse, TechnicalSummary } from '../services/technicalApi';

const TechnicalDashboard: React.FC = () => {
  const [powerIrradianceData, setPowerIrradianceData] = useState<PowerIrradianceResponse | null>(null);
  const [dailyExportData, setDailyExportData] = useState<DailyExportResponse | null>(null);
  const [summary, setSummary] = useState<TechnicalSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');

  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const hours = timeRange === '24h' ? 24 : timeRange === '48h' ? 48 : 72;
      // Sequential calls to reduce concurrent DB load while keeping UI fresh
      const powerIrradiance = await technicalApi.getPowerIrradianceData(hours);
      const dailyExport = await technicalApi.getDailyExportTrends(7);
      const summaryData = await technicalApi.getTechnicalSummary();
      
      setPowerIrradianceData(powerIrradiance);
      setDailyExportData(dailyExport);
      setSummary(summaryData);
      
    } catch (error) {
      console.error('Error fetching technical dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Technical Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Real-time performance correlation and energy export analysis
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="48h">Last 48 Hours</option>
            <option value="72h">Last 72 Hours</option>
          </select>
          
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <BoltIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {summary?.current_power_kw.toFixed(1) || '0.0'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">kW</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Current Power</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Live Generation</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
              <SunIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {summary?.current_irradiance_wm2.toFixed(0) || '0'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">W/m²</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Solar Irradiance</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Current Conditions</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {summary?.today_energy_kwh.toFixed(0) || '0'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">kWh</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Today's Energy</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Total Generation</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {((summary?.performance_ratio || 0) * 100).toFixed(1)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">%</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Performance Ratio</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">System Efficiency</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Power vs Irradiance Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <PowerIrradianceChart
            data={powerIrradianceData?.data || []}
            correlation={powerIrradianceData?.correlation_coefficient || 0}
            loading={loading}
          />
        </div>

        {/* Daily Export Trends Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <GenerationTrendChart
            data={dailyExportData?.data || []}
            loading={loading}
          />
        </div>
      </div>

      {/* Performance Insights */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Performance Insights
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">
              {((powerIrradianceData?.correlation_coefficient || 0) * 100).toFixed(1)}%
            </div>
            <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">
              Power-Irradiance Correlation
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {(powerIrradianceData?.correlation_coefficient || 0) > 0.8 ? 'Excellent' : 
               (powerIrradianceData?.correlation_coefficient || 0) > 0.6 ? 'Good' : 'Needs Attention'}
            </div>
          </div>
          
          <div className="text-center p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-2">
              {dailyExportData?.data.length > 0 ? 
                (dailyExportData.data.reduce((sum, item) => sum + item.export_efficiency, 0) / dailyExportData.data.length).toFixed(1) : 
                '0.0'
              }%
            </div>
            <div className="text-sm text-green-600 dark:text-green-400 font-medium">
              Average Export Efficiency
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Last 7 Days
            </div>
          </div>
          
          <div className="text-center p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-2">
              {dailyExportData?.data.reduce((sum, item) => sum + item.total_export_kwh, 0).toFixed(0) || '0'}
            </div>
            <div className="text-sm text-purple-600 dark:text-purple-400 font-medium">
              Total Weekly Export
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              kWh
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalDashboard;
